import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';

import type { WithChildren } from 'common/types';
import { getWorkspace, resetWorkspace } from './api';
import SessionRotationBridge from './SessionRotationBridge';
import type {
  IKitWorkspace, IKit, IAssembly, IWorkspaceTotals, IPendingAssembly,
} from './types';

const STORAGE_KEY = 'cpq_workspace_key';
const PENDING_STORAGE_KEY = 'cpq_pending_assemblies';
// Registry entries are keyed by sessionId; abandoned sessions never come back,
// so keep only the newest few.
const PENDING_MAX_ENTRIES = 8;
const POLL_INTERVAL_MS = 2000;

const EMPTY_TOTALS: IWorkspaceTotals = {
  kits: 0, configs: 0, assemblyUnits: 0, looseUnits: 0,
};

/**
 * localStorage may be blocked (private mode / embedded iframe). Fall back to an
 * in-memory key for the tab lifetime; refresh-persistence just won't work.
 */
const readStoredKey = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredKey = (key: string | null) => {
  try {
    if (key === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, key);
  } catch {
    // ignore — in-memory state still holds the key for this tab
  }
};

type PendingAssemblyMap = Record<string, IPendingAssembly>;

const readPendingMap = (): PendingAssemblyMap => {
  try {
    const raw = localStorage.getItem(PENDING_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PendingAssemblyMap) : {};
  } catch {
    return {};
  }
};

const writePendingMap = (map: PendingAssemblyMap) => {
  try {
    localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore — the in-memory ref still holds the map for this tab
  }
};

interface IKitWorkspaceContext {
  workspaceKey: string | null;
  workspace: IKitWorkspace | undefined;
  kits: IKit[];
  standalone: IAssembly[];
  totals: IWorkspaceTotals;
  isLoading: boolean;
  isCompleting: boolean;
  refetch: () => Promise<unknown>;
  setWorkspaceKey: (key: string) => void;
  clearWorkspaceKey: () => void;
  storageAvailable: boolean;
  // Pending-assembly registry: which assembly a client session was committed
  // as, so a confirm-page remount updates that row instead of duplicating it,
  // and SessionRotationBridge knows the session must rotate on the next edit.
  getPendingAssembly: (sessionId: string) => IPendingAssembly | null;
  rememberPendingAssembly: (sessionId: string, entry: IPendingAssembly) => void;
  // Confirm-page presence: edits made while it is mounted refine the pending
  // assembly in place and must not rotate the session.
  isConfirmPageMounted: () => boolean;
  setConfirmPageMounted: (mounted: boolean) => void;
}

const KitWorkspaceContext = createContext<IKitWorkspaceContext | undefined>(undefined);

export const KitWorkspaceStorage = ({ children }: WithChildren) => {
  const [workspaceKey, setKey] = useState<string | null>(readStoredKey);
  const [storageAvailable] = useState<boolean>(() => {
    try {
      const probe = '__cpq_probe__';
      localStorage.setItem(probe, '1');
      localStorage.removeItem(probe);
      return true;
    } catch {
      return false;
    }
  });

  const {
    data: workspace, refetch, error, isLoading,
  } = useQuery<IKitWorkspace>({
    queryKey: ['kit-workspace', workspaceKey],
    queryFn: () => getWorkspace(workspaceKey!),
    enabled: !!workspaceKey,
    retry: false,
    // Poll while the combined PDF renders async on the backend; stop once it
    // settles. Unlike the old group flow, the key is NOT cleared after PDF.
    refetchInterval: (data) => (data?.status === 'completing' ? POLL_INTERVAL_MS : false),
  });

  // Drop a key the backend no longer knows (404 from cron cleanup / expiry).
  useEffect(() => {
    const status = error && typeof error === 'object' && 'status' in error
      ? (error as { status: number }).status
      : undefined;
    if (status === 404) {
      writeStoredKey(null);
      setKey(null);
    }
  }, [error]);

  // Refresh after a finished order = start fresh: if the very first load returns
  // an already-completed workspace, reset it (wipe assemblies, keep empty kits)
  // so the wizard begins anew. Fires once, on the initial load only — the
  // in-session draft->completed flip is unaffected (the first load was a draft).
  const initialHandledRef = useRef(false);
  useEffect(() => {
    if (initialHandledRef.current || !workspace) return;
    initialHandledRef.current = true;
    if (workspace.status === 'completed' && workspaceKey) {
      resetWorkspace(workspaceKey).then(() => refetch()).catch(() => {});
    }
  }, [workspace, workspaceKey, refetch]);

  const setWorkspaceKey = useCallback((key: string) => {
    writeStoredKey(key);
    setKey(key);
  }, []);

  const clearWorkspaceKey = useCallback(() => {
    writeStoredKey(null);
    setKey(null);
  }, []);

  // Stable accessor for the bridge: reads the fresh key without re-registering
  // its interceptor on every key change.
  const workspaceKeyRef = useRef(workspaceKey);
  workspaceKeyRef.current = workspaceKey;
  const getWorkspaceKey = useCallback(() => workspaceKeyRef.current, []);

  // Refs, not state: registry lookups happen inside commit/update flows and
  // must never re-render the tree.
  const pendingMapRef = useRef<PendingAssemblyMap>(readPendingMap());
  const confirmMountedRef = useRef(false);

  const getPendingAssembly = useCallback((sessionId: string): IPendingAssembly | null => (
    pendingMapRef.current[sessionId] ?? null
  ), []);

  const rememberPendingAssembly = useCallback((sessionId: string, entry: IPendingAssembly) => {
    const next: PendingAssemblyMap = { ...pendingMapRef.current };
    // Re-insert so the entry becomes the newest for pruning purposes.
    delete next[sessionId];
    next[sessionId] = entry;
    const keys = Object.keys(next);
    keys.slice(0, Math.max(0, keys.length - PENDING_MAX_ENTRIES)).forEach((stale) => {
      delete next[stale];
    });
    pendingMapRef.current = next;
    writePendingMap(next);
  }, []);

  const isConfirmPageMounted = useCallback(() => confirmMountedRef.current, []);
  const setConfirmPageMounted = useCallback((mounted: boolean) => {
    confirmMountedRef.current = mounted;
  }, []);

  const value = useMemo<IKitWorkspaceContext>(() => ({
    workspaceKey,
    workspace,
    kits: workspace?.kits ?? [],
    standalone: workspace?.standalone ?? [],
    totals: workspace?.totals ?? EMPTY_TOTALS,
    isLoading,
    isCompleting: workspace?.status === 'completing',
    refetch,
    setWorkspaceKey,
    clearWorkspaceKey,
    storageAvailable,
    getPendingAssembly,
    rememberPendingAssembly,
    isConfirmPageMounted,
    setConfirmPageMounted,
  }), [
    workspaceKey, workspace, isLoading, refetch, setWorkspaceKey, clearWorkspaceKey, storageAvailable,
    getPendingAssembly, rememberPendingAssembly, isConfirmPageMounted, setConfirmPageMounted,
  ]);

  return (
    <KitWorkspaceContext.Provider value={value}>
      <SessionRotationBridge
        getPendingAssembly={getPendingAssembly}
        isConfirmPageMounted={isConfirmPageMounted}
        getWorkspaceKey={getWorkspaceKey}
      />
      {children}
    </KitWorkspaceContext.Provider>
  );
};

export const useKitWorkspace = (): IKitWorkspaceContext => {
  const ctx = useContext(KitWorkspaceContext);
  if (!ctx) {
    throw new Error('useKitWorkspace must be used inside <KitWorkspaceStorage>');
  }
  return ctx;
};
